import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { communityRegions } from '~/api/community/constants';
import { Button, Drawer, FilterChips } from '~/components';
import { Checkbox, Field, Input, Select } from '~/components/Form';
import { availablePrimaryActivities } from '~/constants/constants';
import { useIntl } from '~/hooks/useIntl';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';

import { menuStyle, searchStyle } from './CommunityFilters.css';
import { useCommunityProfileFilters } from './NetworkOverview.nuqs';

export const CommunityFilters = () => {
  const { t } = useTranslation(['common', 'community']);
  const { compareForSort } = useIntl();

  const filters = useCommunityProfileFilters();

  const isMobile = useMediaQuery(breakpoints.small);

  const activityOptions = useMemo(
    () => availablePrimaryActivities.map((activity) => ({ label: t(`common:activities.${activity}.label`), value: activity })),
    [t],
  );
  const regionOptions = useMemo(
    () =>
      communityRegions
        .map((region) => ({ label: t(`community:regions.${region}`), value: region }))
        .sort(({ label: a }, { label: b }) => compareForSort(a, b)),
    [compareForSort, t],
  );

  const activeFilterCount = [filters.activityIds.value.length > 0, filters.regions.value.length > 0, filters.isOpenToWork.value].filter(
    Boolean,
  ).length;

  return (
    <div className={menuStyle}>
      <Field.Root className={searchStyle}>
        <Field.Label srOnly>{t('community:overview.filters.name.placeholder')}</Field.Label>
        <Input
          clearable
          icon="Search"
          onValueChange={(value) => filters.search.onChange(value)}
          placeholder={t('community:overview.filters.name.placeholder')}
          type="search"
          value={filters.search.value}
        />
      </Field.Root>

      {isMobile ? (
        <Drawer.Root>
          <Drawer.Trigger
            render={
              <Button emphasis="secondary" icon="Filter">
                {t('common:datatable.showFilters')}
                {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            }
          />

          <Drawer.Popup>
            <Drawer.Header title={t('common:datatable.filters')} />

            <Drawer.Content>
              <CommunityFilterControls activityOptions={activityOptions} regionOptions={regionOptions} />
              <CommunityFilterChips activityOptions={activityOptions} regionOptions={regionOptions} />
            </Drawer.Content>

            <Drawer.Actions>
              <Drawer.Close render={<Button>{t('common:datatable.showResults')}</Button>} />
            </Drawer.Actions>
          </Drawer.Popup>
        </Drawer.Root>
      ) : (
        <>
          <CommunityFilterControls activityOptions={activityOptions} regionOptions={regionOptions} />
          <CommunityFilterChips activityOptions={activityOptions} regionOptions={regionOptions} />
        </>
      )}
    </div>
  );
};

type ActivityOption = { label: string; value: (typeof availablePrimaryActivities)[number] };
type RegionOption = { label: string; value: (typeof communityRegions)[number] };

type FilterOptionsProps = {
  activityOptions: ActivityOption[];
  regionOptions: RegionOption[];
};

const CommunityFilterControls = ({ activityOptions, regionOptions }: FilterOptionsProps) => {
  const { t } = useTranslation(['community']);

  const filters = useCommunityProfileFilters();

  return (
    <>
      <Field.Root>
        <Field.Label srOnly>{t('community:overview.filters.activity.label')}</Field.Label>
        <Select.Root
          items={activityOptions}
          multiple
          onValueChange={(value) => filters.activityIds.onChange(value)}
          value={filters.activityIds.value}
        >
          <Select.Button placeholder={t('community:overview.filters.activity.label')}>
            {t('community:overview.filters.activity.label')}
          </Select.Button>

          <Select.List align="end" minWidth="200px">
            {activityOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.List>
        </Select.Root>
      </Field.Root>

      <Field.Root>
        <Field.Label srOnly>{t('community:overview.filters.region.label')}</Field.Label>
        <Select.Root
          items={regionOptions}
          multiple
          onValueChange={(value) => filters.regions.onChange(value)}
          value={filters.regions.value}
        >
          <Select.Button placeholder={t('community:overview.filters.region.label')}>
            {t('community:overview.filters.region.label')}
          </Select.Button>

          <Select.List align="end" minWidth="200px">
            {regionOptions.map((option) => (
              <Select.Item key={String(option.value)} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.List>
        </Select.Root>
      </Field.Root>

      <Field.Root>
        <Checkbox.Root checked={filters.isOpenToWork.value} onCheckedChange={filters.isOpenToWork.onChange}>
          <Checkbox.Label>{t('community:overview.filters.openToWork.text')}</Checkbox.Label>
        </Checkbox.Root>
      </Field.Root>
    </>
  );
};

const CommunityFilterChips = ({ activityOptions, regionOptions }: FilterOptionsProps) => {
  const { t } = useTranslation(['community']);

  const filters = useCommunityProfileFilters();

  const hasFilters = filters.activityIds.value.length > 0 || filters.regions.value.length > 0 || filters.isOpenToWork.value;

  if (!hasFilters) return null;

  return (
    <FilterChips.Root>
      {filters.activityIds.value.map((activityId) => (
        <FilterChips.Chip
          key={`activity-${activityId}`}
          onRemove={() => filters.activityIds.onChange(filters.activityIds.value.filter((value) => value !== activityId))}
        >
          {activityOptions.find((option) => option.value === activityId)?.label ?? `${activityId}`}
        </FilterChips.Chip>
      ))}

      {filters.regions.value.map((region) => (
        <FilterChips.Chip
          key={`region-${region}`}
          onRemove={() => filters.regions.onChange(filters.regions.value.filter((value) => value !== region))}
        >
          {regionOptions.find((option) => option.value === region)?.label ?? region}
        </FilterChips.Chip>
      ))}

      {filters.isOpenToWork.value && (
        <FilterChips.Chip onRemove={() => filters.isOpenToWork.onChange(false)}>
          {t('community:overview.filters.openToWork.text')}
        </FilterChips.Chip>
      )}
    </FilterChips.Root>
  );
};
